import React, { useMemo } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar';
import { cssMap } from '@atlaskit/css';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import ProfileCardTrigger from '@atlaskit/profilecard/user';
import { token } from '@atlaskit/tokens';

import { createProfileClient } from '../../../../../../../knowledge-cards/create-profile-client';
import { trimARI } from '../../../../../../../knowledge-cards/trim-ari';
import { KnowledgeCardsIntentType } from '../../../../../../../knowledge-cards/types';
import { useTeamsAppLinks } from '../../../../../../../knowledge-cards/use-teams-app-links';
import type { ConfluencePageContributor } from '../../../../../api/useTwgSources/gql/types';
import { messages } from '../../messages';

const styles = cssMap({
	pressable: {
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
		backgroundColor: token('color.background.neutral.subtle'),
	},
	contributorCard: {
		height: '40px',
	},
	contributorCardContent: {
		textAlign: 'left',
	},
	contributorsCardSubtitle: {
		'&::first-letter': {
			textTransform: 'capitalize',
		},
	},
});

export const ContributorCard = ({
	contributor,
	cloudId,
	onContributorClick,
}: {
	contributor: ConfluencePageContributor;
	cloudId: string;
	onContributorClick: (action: string) => void;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { toUser } = useTeamsAppLinks({
		type: KnowledgeCardsIntentType.PERSON,
		currentUserId: contributor.data.accountId || '',
		cloudId,
	});

	const profileClient = useMemo(() => createProfileClient(cloudId), [cloudId]);

	const onContributorClickHandler = (e?: React.MouseEvent | React.KeyboardEvent) => {
		onContributorClick('collaborator-link');
		toUser(contributor.data.accountId || '').onNavigate(e);
	};

	return (
		<ProfileCardTrigger
			userId={trimARI(contributor.data.accountId || '')}
			resourceClient={profileClient}
			actions={[
				{
					label: formatMessage(messages.contributors_card_hover_profile_link),
					link: toUser(contributor.data.accountId || '').href,
					callback: onContributorClickHandler,
				},
			]}
			ariaLabel={contributor.data.name}
			position="bottom-end"
		>
			<Pressable onClick={onContributorClickHandler} xcss={styles.pressable}>
				<Inline space="space.100" xcss={styles.contributorCard} testId="contributor-card">
					<Avatar size="small" name={contributor.data.name} src={contributor.data.picture} />
					<Stack xcss={styles.contributorCardContent}>
						<Text color="color.text" maxLines={1}>
							{contributor.data.name}
						</Text>
						<Box xcss={styles.contributorsCardSubtitle}>
							<Text size="small" color="color.text.subtlest" maxLines={1}>
								<FormattedMessage
									{...messages.contributors_card_subtitle}
									values={{ type: contributor.type }}
								/>
							</Text>
						</Box>
					</Stack>
				</Inline>
			</Pressable>
		</ProfileCardTrigger>
	);
};
